import {
  Button,
  ButtonGroup,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography
} from '@mui/material'
import dayjs, { Dayjs } from 'dayjs'
import React, { FC, useEffect } from 'react'

import { RruleBydayEnum, RruleFreqEnum } from '../../sdk'
import {
  getDateDetails,
  numberToDayEnum
} from '../../utils/dateMappings/dateMappings'
import TextFieldNoArrowNoScroll from '../FormField/TextFieldNoArrowNoScroll'
import { DatePickerWithAlert } from './DatePickerWithAlert'

interface Props {
  dtstart: Dayjs | null
  setDtstart: React.Dispatch<React.SetStateAction<dayjs.Dayjs | null>>
  repeatFrequency: number
  setRepeatFrequency: React.Dispatch<React.SetStateAction<number>>
  repeatFrequencyUnit: RruleFreqEnum
  setRepeatFrequencyUnit: React.Dispatch<React.SetStateAction<RruleFreqEnum>>
  selectedByday: RruleBydayEnum
  setSelectedByday: React.Dispatch<React.SetStateAction<RruleBydayEnum>>
  monthlyType: 'date' | 'day'
  setMonthlyType: React.Dispatch<React.SetStateAction<'date' | 'day'>>
  setError: React.Dispatch<React.SetStateAction<boolean>>
}

export const RepeatSendPicker: FC<Props> = ({
  dtstart,
  setDtstart,
  repeatFrequency,
  setRepeatFrequency,
  repeatFrequencyUnit,
  setRepeatFrequencyUnit,
  selectedByday,
  setSelectedByday,
  monthlyType,
  setMonthlyType,
  setError,
  ...props
}) => {
  useEffect(() => {
    if (
      dtstart === null ||
      dtstart?.toString() === 'Invalid Date' ||
      repeatFrequency <= 0
    ) {
      setError(true)
    } else {
      setError(false)
    }
  }, [dtstart, repeatFrequency, setError])

  const displayValues = {
    [RruleFreqEnum.Daily]: 'day',
    [RruleFreqEnum.Weekly]: 'week',
    [RruleFreqEnum.Monthly]: 'month',
    [RruleFreqEnum.Yearly]: 'year'
  }

  // reset values accordingly
  useEffect(() => {
    setSelectedByday(
      dtstart !== null && dtstart?.toString() !== 'Invalid Date'
        ? numberToDayEnum(dtstart.day())
        : RruleBydayEnum.Mo
    )
  }, [dtstart, repeatFrequencyUnit, setSelectedByday])
  useEffect(() => {
    setMonthlyType('date')
  }, [repeatFrequencyUnit, setMonthlyType])

  return (
    <Grid container spacing={2}>
      <Grid container item xs={12} spacing={2} alignItems="center">
        <Grid item container xs={4} justifyContent="flex-end">
          <Typography>Start Date:</Typography>
        </Grid>
        <Grid item container xs={8} justifyContent="flex-start">
          {/* should allow for past dates */}
          <DatePickerWithAlert
            label={'Recurrence Start Date'}
            alertCondition={
              dtstart === null || dtstart?.toString() === 'Invalid Date'
            }
            alertText={'No date chosen'}
            value={dtstart}
            onChange={(newDate) => {
              setDtstart(newDate)
            }}
            canClear={false}
            withTime={false}
            sx={{ width: 300 }}
          />
        </Grid>
      </Grid>

      <Grid container item xs={12} spacing={2} alignItems="center">
        <Grid item container xs={4} justifyContent="flex-end">
          <Typography>Repeat every:</Typography>
        </Grid>
        <Grid item container xs={8} justifyContent="flex-start">
          <Stack direction={'row'} alignItems="center" {...props}>
            <Tooltip
              title={repeatFrequency <= 0 ? 'Must be greater than 1' : ''}>
              <TextFieldNoArrowNoScroll
                label="Frequency"
                value={repeatFrequency}
                error={repeatFrequency <= 0}
                type="number"
                onChange={(event) => {
                  setRepeatFrequency(parseInt(event.target.value) || 0)
                }}
                sx={{ width: 150 }}
              />
            </Tooltip>

            <FormControl variant="outlined" sx={{ width: 150 }}>
              <InputLabel id="frequency-units-label">Units</InputLabel>
              <Select
                labelId="frequency-units-label"
                id="frequency-units-select"
                value={repeatFrequencyUnit}
                onChange={(event) => {
                  setRepeatFrequencyUnit(event.target.value as RruleFreqEnum)
                }}
                label="Units"
                variant="outlined">
                {[
                  RruleFreqEnum.Daily,
                  RruleFreqEnum.Weekly,
                  RruleFreqEnum.Monthly,
                  RruleFreqEnum.Yearly
                ].map((value) => (
                  <MenuItem key={value} value={value}>
                    {displayValues[value]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Grid>
      </Grid>

      {repeatFrequencyUnit === RruleFreqEnum.Weekly && (
        <Grid container item xs={12} spacing={2} alignItems="center">
          <Grid item container xs={4} justifyContent="flex-end"></Grid>
          <Grid item container xs={8} justifyContent="flex-start">
            <ButtonGroup sx={{ width: 300 }}>
              {Object.entries(RruleBydayEnum)
                .filter(([key]) => !key.includes('_'))
                .map(([key, value]) => (
                  <Button
                    key={key}
                    variant={selectedByday === value ? 'contained' : 'outlined'}
                    onClick={() => setSelectedByday(value as RruleBydayEnum)}>
                    {key}
                  </Button>
                ))}
            </ButtonGroup>
          </Grid>
        </Grid>
      )}

      {repeatFrequencyUnit === RruleFreqEnum.Monthly && (
        <Grid container item xs={12} spacing={2} alignItems="center">
          <Grid item container xs={4} justifyContent="flex-end"></Grid>
          <Grid item container xs={8} justifyContent="flex-start">
            <Select
              id="monthly-type-select"
              value={monthlyType}
              onChange={(event) => {
                setMonthlyType(event.target.value as 'date' | 'day')
              }}
              sx={{ width: 300 }}>
              <MenuItem key={'date'} value={'date'}>
                Monthly on day {dtstart?.date()}
              </MenuItem>
              <MenuItem key={'day'} value={'day'}>
                Monthly on the {getDateDetails(dtstart)}
              </MenuItem>
            </Select>
          </Grid>
        </Grid>
      )}
    </Grid>
  )
}
